import axios from "axios";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { SelectOptionType } from "types/select/SelectOptionType";
import Select, { SingleValue } from "react-select";
import _lodash from "lodash";
import { ScaleLoader } from "react-spinners";
import { toast } from "react-hot-toast";
import { InputErrorMessage } from "../input/InputErrorMessage";
import { CustomLabel } from "../input/CustomLabel";
import { Controller, Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { possuiMensagemDeErro } from "shared/util/functions/errors/RequestPossuiMensagemDeErro";
import { getMensagemDeErroBackend } from "shared/util/functions/errors/GetMensagemDeErroBackend";

// Tipos de props para a versão sem validação e com validação
type SelectAsyncPropsWithoutValidationProps = {
  usarValidacaoParaFormulario: false;
};

type SelectAsyncPropsWithValidationProps = {
  usarValidacaoParaFormulario: true;
  /**Nome do atributo que será usado no formulario para realizar as validações*/
  name: string;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
};

type SelectAsyncProps<T> = {
  id: string;
  nomeAtributoValue: string;
  nomeAtributoLabel: string;
  placeholder?: string;
  endpoint: string;
  invalidateQuerysKey?: any[];
  className?: string;
  label?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  atributosRefresh?: any[];
  possuiErro?: boolean;
  required?: boolean;
  onChange?: (opcaoSelecionada: SingleValue<SelectOptionType<T>>) => void;
  defaultValue?: SingleValue<SelectOptionType<T>>;
} & (SelectAsyncPropsWithoutValidationProps | SelectAsyncPropsWithValidationProps);

const SelectAsync = <T extends {}>(props: SelectAsyncProps<T>) => {
  const prevProps = useRef<SelectAsyncProps<T>>();
  const [options, setOptions] = useState<SelectOptionType<T>[]>([]);
  const [deveBuscarOpcoes, setDeveBuscarOpcoes] = useState(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (prevProps.current && prevProps.current !== props) {
      if (atributosRefreshSofreuAlteracao(prevProps.current, props)) {
        setDeveBuscarOpcoes(true);
      }
    }
    prevProps.current = props;
  });

  const atributosRefreshSofreuAlteracao = (prevProps: SelectAsyncProps<T>, props: SelectAsyncProps<T>) => {
    const prevAtributesRefresh = prevProps.atributosRefresh;
    const atributesRefreshAtuais = props.atributosRefresh;

    if (prevAtributesRefresh?.length !== atributesRefreshAtuais?.length) {
      return true;
    }

    return !_lodash.isEqual(prevAtributesRefresh, atributesRefreshAtuais);
  };

  const customMenuOpen = async () => {
    if (deveBuscarOpcoes) {
      setOptions([]);
      await loadOptions();
      setDeveBuscarOpcoes(false);
    }
  };

  const loadOptions = async () => {
    setIsLoading(true);
    await axios
      .get(props.endpoint)
      .then((resp) => {
        const registros = resp.data?.content || resp.data?.payload || resp.data || Array.isArray(resp.data) || [];

        const options: SelectOptionType<any>[] = registros.map((registro: Record<string, any>) => ({
          value: _lodash.get(registro, props.nomeAtributoValue),
          label: _lodash.get(registro, props.nomeAtributoLabel),
        }));
        setOptions(options);
      })
      .catch((error) => {
        if (possuiMensagemDeErro(error)) {
          toast.error(getMensagemDeErroBackend(error));
        } else {
          toast.error("Erro ao buscar opções");
        }
      })
      .finally(() => setIsLoading(false));
  };

  const renderLoadingMessage = () => {
    return (
      <div>
        <span style={{ fontWeight: "bold" }}>Carregando opções</span>
        <ScaleLoader height="10px" color="#83cf9c" />
      </div>
    );
  };

  const getCustomStyle = (possuiErro: boolean): object => {
    return {
      control: (provided: any, state: any) => ({
        ...provided,
        borderColor: possuiErro ? "#dc3545 !important" : state.isFocused ? "#83cf9c !important" : "#c4c4c4",
        boxShadow: "none",
        fontSize: "small !important",
        fontWeight: "200",
      }),
    };
  };

  const renderSelectAsyncComValidacao = (): ReactNode => {
    if (props.usarValidacaoParaFormulario) {
      return (
        <Controller
          name={props.name}
          control={props.control}
          defaultValue={props.defaultValue}
          render={({ field, fieldState }) => (
            <>
              {props.label && (
                <CustomLabel possuiErro={fieldState.error != null}>
                  {props.required ? `${props.label} *` : props.label}
                </CustomLabel>
              )}
              <Select
                {...props.register(props.name)}
                {...field}
                id={props.id}
                styles={getCustomStyle(fieldState.error != null)}
                isClearable={props.isClearable}
                blurInputOnSelect
                closeMenuOnSelect
                className={props.className || ""}
                isDisabled={props.isDisabled}
                placeholder={props.placeholder || ""}
                onMenuOpen={() => customMenuOpen()}
                isLoading={isLoading}
                loadingMessage={() => renderLoadingMessage()}
                noOptionsMessage={() => "Nenhum registro foi encontrado"}
                options={options}
                value={field.value || null}
                onChange={(opcaoSelecionada: SelectOptionType<T>) => {
                  field.onChange(opcaoSelecionada);
                  props.setValue(props.name, opcaoSelecionada);
                  props.onChange && props.onChange(opcaoSelecionada);
                }}
              />
              {fieldState.error && <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>}
            </>
          )}
        />
      );
    }
  };

  const renderSelectAsyncSemValidacao = (): ReactNode => {
    if (!props.usarValidacaoParaFormulario) {
      return (
        <>
          {props.label && <CustomLabel>{props.required ? `${props.label} *` : props.label}</CustomLabel>}
          <Select
            id={props.id}
            styles={getCustomStyle(false)}
            isClearable={props.isClearable}
            blurInputOnSelect
            closeMenuOnSelect
            className={props.className || ""}
            isDisabled={props.isDisabled}
            placeholder={props.placeholder || ""}
            onMenuOpen={() => customMenuOpen()}
            isLoading={isLoading}
            loadingMessage={() => renderLoadingMessage()}
            noOptionsMessage={() => "Nenhum registro foi encontrado"}
            options={options}
            onChange={(opcaoSelecionada: SingleValue<SelectOptionType<T>>) =>
              props.onChange && props.onChange(opcaoSelecionada)
            }
            defaultValue={props.defaultValue}
          />
        </>
      );
    }
  };

  return (
    <>
      {props.usarValidacaoParaFormulario && renderSelectAsyncComValidacao()}
      {!props.usarValidacaoParaFormulario && renderSelectAsyncSemValidacao()}
    </>
  );
};

export default SelectAsync;
